<template>
  <div
    class="base-range-slider"
    :class="{
      'base-range-slider--compact': props.compact,
      'base-range-slider--with-chart': hasHistogram,
    }"
  >
    <div v-if="hasHistogram" class="base-range-slider__chart">
      <span
        v-for="(bar, idx) in normalizedBars"
        :key="idx"
        class="base-range-slider__bar"
        :class="{ 'base-range-slider__bar--active': isBarActive(idx) }"
        :style="{ height: bar + '%' }"
      />
    </div>

    <vue-slider
      v-model="sliderValue"
      :min="props.min"
      :max="props.max"
      :interval="props.step"
      :enable-cross="false"
      :tooltip="'none'"
      :dot-size="10"
      :height="4"
      :silent="true"
      :drag-on-click="false"
      @drag-end="onDragEnd"
      @change="onSliderChange"
      @dragging="onDragging"
    />

    <div class="base-range-slider__inputs">
      <span
        class="base-range-slider__input"
        :style="{ '--filter-after-content': `'${props.suffix ?? ''}'` }"
      >
        <input
          v-model="inputMin"
          type="text"
          inputmode="decimal"
          :maxlength="props.maxlength || 10"
          @input="onMinInput"
          @blur="commitInput"
          @keydown.enter="commitInput"
        >
      </span>
      <span class="base-range-slider__sep" aria-hidden="true">–</span>
      <span
        class="base-range-slider__input"
        :style="{ '--filter-after-content': `'${props.suffix ?? ''}'` }"
      >
        <input
          v-model="inputMax"
          type="text"
          inputmode="decimal"
          :maxlength="props.maxlength || 10"
          @input="onMaxInput"
          @blur="commitInput"
          @keydown.enter="commitInput"
        >
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import VueSlider from "vue-3-slider-component";
import { onlyNumbers } from "~/utilities/helpers/format-data/onlyNumbers";

interface Props {
  modelValue: [number, number];
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  histogram?: number[];
  compact?: boolean;
  maxlength?: number;
}

const props = withDefaults(defineProps<Props>(), {
  step: 0.1,
  suffix: "",
  compact: false,
  histogram: () => [],
  maxlength: 10,
});

interface Emits {
  (event: "update:modelValue", value: [number, number]): void;
  (event: "change", value: [number, number]): void;
  (event: "drag-end", value: [number, number]): void;
  (event: "dragging", value: [number, number]): void;
}

const emit = defineEmits<Emits>();

const sliderValue = ref<[number, number]>([
  props.modelValue?.[0] ?? props.min,
  props.modelValue?.[1] ?? props.max,
]);
const inputMin = ref<string>(formatNum(sliderValue.value[0]));
const inputMax = ref<string>(formatNum(sliderValue.value[1]));

function formatNum(n: number): string {
  if (n === undefined || n === null || Number.isNaN(n)) return "";
  return Number.isInteger(n) ? n.toString() : n.toFixed(1);
}

function clamp(value: number, lo: number, hi: number): number {
  if (Number.isNaN(value)) return lo;
  return Math.min(Math.max(value, lo), hi);
}

watch(
  () => props.modelValue,
  (next) => {
    if (!next) return;
    const a = next[0] ?? props.min;
    const b = next[1] ?? props.max;
    if (a === sliderValue.value[0] && b === sliderValue.value[1]) return;
    sliderValue.value = [a, b];
    inputMin.value = formatNum(a);
    inputMax.value = formatNum(b);
  },
  { deep: true }
);

watch(
  () => [props.min, props.max],
  () => {
    sliderValue.value = [
      clamp(sliderValue.value[0], props.min, props.max),
      clamp(sliderValue.value[1], props.min, props.max),
    ];
    inputMin.value = formatNum(sliderValue.value[0]);
    inputMax.value = formatNum(sliderValue.value[1]);
  }
);

const onSliderChange = (next: number[]) => {
  if (!Array.isArray(next) || next.length < 2) return;
  inputMin.value = formatNum(next[0]);
  inputMax.value = formatNum(next[1]);
  emit("update:modelValue", [next[0], next[1]]);
};

const onDragging = (next: number[]) => {
  if (!Array.isArray(next) || next.length < 2) return;
  emit("dragging", [next[0], next[1]]);
};

const onDragEnd = () => {
  const v: [number, number] = [sliderValue.value[0], sliderValue.value[1]];
  emit("drag-end", v);
  emit("change", v);
};

const onMinInput = () => {
  const sanitized = onlyNumbers(inputMin.value, true);
  nextTick(() => {
    inputMin.value = sanitized;
  });
};

const onMaxInput = () => {
  const sanitized = onlyNumbers(inputMax.value, true);
  nextTick(() => {
    inputMax.value = sanitized;
  });
};

const commitInput = () => {
  let a = parseFloat(inputMin.value);
  let b = parseFloat(inputMax.value);
  if (Number.isNaN(a)) a = props.min;
  if (Number.isNaN(b)) b = props.max;
  a = clamp(a, props.min, props.max);
  b = clamp(b, props.min, props.max);
  if (a > b) [a, b] = [b, a];
  sliderValue.value = [a, b];
  inputMin.value = formatNum(a);
  inputMax.value = formatNum(b);
  emit("update:modelValue", [a, b]);
  emit("change", [a, b]);
};

const hasHistogram = computed(
  () => Array.isArray(props.histogram) && props.histogram.length > 0
);

const normalizedBars = computed<number[]>(() => {
  if (!hasHistogram.value) return [];
  const peak = Math.max(...props.histogram, 1);
  return props.histogram.map((v) => Math.max(6, Math.round((v / peak) * 100)));
});

const isBarActive = (idx: number): boolean => {
  if (!hasHistogram.value) return false;
  const n = props.histogram.length;
  const span = props.max - props.min || 1;
  const t = (idx + 0.5) / n;
  const price = props.min + t * span;
  return price >= sliderValue.value[0] && price <= sliderValue.value[1];
};
</script>

<style scoped lang="scss">
.base-range-slider {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  // Without min-width: 0 a grid/flex parent grows to fit the component's
  // intrinsic content (vue-slider rail + handles), bleeding out of narrow
  // containers like the top-bar popover (260px column).
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  &__chart {
    display: flex;
    align-items: flex-end;
    gap: 2px;
    height: 36px;
    width: 100%;
    min-width: 0;
    padding: 0 4px;
    box-sizing: border-box;
  }

  &__bar {
    flex: 1 1 0;
    min-width: 0;
    background: var(--gray-monochrome-40, #e0e0e0);
    border-radius: 2px 2px 0 0;
    transition: background-color 0.2s ease;

    &--active {
      background: var(--black-monochrome-72, #9ea7b9);
    }
  }

  :deep(.vue-slider) {
    padding: 8px 0 !important;
    width: 100% !important;
    min-width: 0;
    max-width: 100%;
    // NOTE: do NOT add box-sizing: border-box here — the vue-slider library
    // inline-styles `height: <props.height>px` on .vue-slider expecting
    // content-box, so the rail can fill 100% of that height. With border-box
    // the 4px height ends up entirely inside the vertical padding (8+8) and
    // the rail/process collapse to 0px (the black track disappears).
  }

  :deep(.vue-slider-rail) {
    background: var(--border-subtle, rgba(18, 18, 18, 0.15));
    border-radius: 999px;
  }

  :deep(.vue-slider-process) {
    background: var(--black-monochrome);
    border-radius: 999px;
  }

  :deep(.vue-slider-dot-handle) {
    background: var(--black-monochrome);
    border: none;
    box-shadow: 0 1px 3px rgba(18, 18, 18, 0.25);
  }

  :deep(.vue-slider-dot-handle-focus) {
    box-shadow: 0 0 0 5px rgba(18, 18, 18, 0.12);
  }

  &__inputs {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
  }

  &__sep {
    flex: 0 0 auto;
    color: var(--black-monochrome-60, rgba(18, 18, 18, 0.6));
    font-size: 14px;
    user-select: none;
  }

  &__input {
    flex: 1 1 0;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 32px;
    padding: 0 8px;
    border-radius: 6px;
    border: 1px solid var(--border-subtle, rgba(18, 18, 18, 0.2));
    background: var(--surface, #ffffff);

    input {
      flex: 1 1 auto;
      min-width: 0;
      height: 100%;
      background: transparent;
      border: none;
      outline: none;
      padding: 0;
      color: var(--black-monochrome, #121212);
      font-family: inherit;
      // 16px floor prevents iOS Safari from auto-zooming on focus
      font-size: 16px;
      font-weight: 500;
      font-variant-numeric: tabular-nums;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &::after {
      content: var(--filter-after-content);
      flex: 0 0 auto;
      margin-left: 6px;
      color: var(--black-monochrome-60, rgba(18, 18, 18, 0.6));
      font-size: 12px;
      font-weight: 400;
    }
  }

  &--compact {
    gap: 8px;

    .base-range-slider__chart {
      height: 28px;
    }
  }
}
</style>
